import { IconButton, styled } from '@chronosphereio/chrono-ui';
import { DotsHorizontal } from 'mdi-material-ui';
import { useMemo } from 'react';
import { DIFF_BAND_TRUNCATION_SERIES_NAME, UnitOptions } from '@/core';
import { EventTooltip } from '../../EventTooltip';
import { SeriesInfo } from '../SeriesInfo';
import { TooltipConfig, TooltipSeriesArray, TooltipSeriesInfo } from '../tooltip-model';
import { isEvent } from '../utils';

export interface TooltipContentProps {
  series: TooltipSeriesArray | null;
  totalSeries: number;
  tooltipConfig: TooltipConfig;
  isTooltipPinned?: boolean;
  isStacked?: boolean;
  unit?: UnitOptions;
  clearPinned?: () => void;
  /**
   * When true, only renders the non-emphasized series (series further from cursor that are not bold).
   */
  excludeEmphasized?: boolean;
}

export function TooltipContent(props: TooltipContentProps) {
  const { series, totalSeries, tooltipConfig, unit, clearPinned, excludeEmphasized = false } = props;

  // TODO: update to useTooltipContext after legacy LineChartBase is removed
  const {
    mode: tooltipMode,
    wrapSeriesName,
    enableTooltipPreciseValues,
    tooltipContextMenuProps,
    tooltipContextMenuData,
    getSeriesConfig,
    appMetadata,
  } = tooltipConfig;

  const seriesToRender = useMemo(() => {
    if (series === null) return null;

    const newSeriesArray = series.slice();

    // When excludeEmphasized is true, remove the emphasized series from the list.
    // It will be rendered separately by the parent component in EmphasizedSeriesSection.
    if (excludeEmphasized) {
      const emphasizedIndex = newSeriesArray.findIndex((s) => s.shouldEmphasizeText);
      if (emphasizedIndex !== -1) {
        newSeriesArray.splice(emphasizedIndex, 1);
      }
    }

    // Series is already sorted by parent component
    return newSeriesArray;
  }, [series, excludeEmphasized]);

  const isSingleSeriesMode = tooltipMode === 'single' || seriesToRender?.length === 1;

  if (series === null || seriesToRender === null) {
    return null;
  }

  if (isEvent(series)) {
    const eventPayload = series[0]?.event;
    if (eventPayload) {
      return <EventTooltip event={eventPayload} clearPinned={clearPinned} />;
    }
  }

  // TODO: use react-virtuoso to improve performance
  return (
    <TooltipContainer>
      {seriesToRender.map(
        ({
          dataIndex,
          seriesIndex,
          seriesName,
          seriesId,
          x,
          y,
          formattedY,
          markerColor,
          markerType,
          shouldEmphasizeText,
        }) => {
          if (dataIndex === null || seriesIndex === null) return null;
          const key = seriesIndex.toString() + dataIndex.toString();

          // Do not show synthetic truncated confidence interval series
          if (seriesName === DIFF_BAND_TRUNCATION_SERIES_NAME) {
            return null;
          }

          return (
            <SeriesInfo
              key={key}
              seriesId={seriesId}
              seriesName={seriesName}
              seriesIndex={seriesIndex}
              y={y}
              x={x}
              links={tooltipConfig.links}
              formattedY={formattedY}
              markerColor={markerColor}
              markerType={markerType}
              config={getSeriesConfig && getSeriesConfig(seriesId)}
              totalSeries={totalSeries}
              // Show full name when series is being hovered over, wrapSeriesName is set, or in single series mode
              wrapSeriesName={shouldEmphasizeText || (wrapSeriesName ?? isSingleSeriesMode)}
              emphasizeText={shouldEmphasizeText}
              tooltipContextMenuProps={tooltipContextMenuProps}
              tooltipContextMenuData={tooltipContextMenuData}
              isTooltipPinned={props.isTooltipPinned}
              unit={unit}
              clearPinned={clearPinned}
              enableTooltipPreciseValues={enableTooltipPreciseValues}
              appMetadata={appMetadata}
            />
          );
        }
      )}
    </TooltipContainer>
  );
}

/**
 * Renders the emphasized series with a separator above it.
 * This is meant to be rendered in a TooltipStaticContentWrapper to keep it visible.
 */
export function EmphasizedSeriesSection(
  props: TooltipContentProps & { emphasizedSeries: TooltipSeriesInfo; onIncreaseHeight?: () => void }
) {
  const { emphasizedSeries, tooltipConfig, totalSeries, unit, clearPinned, onIncreaseHeight } = props;
  const {
    wrapSeriesName,
    enableTooltipPreciseValues,
    tooltipContextMenuProps,
    tooltipContextMenuData,
    getSeriesConfig,
    appMetadata,
    mode: tooltipMode,
  } = tooltipConfig;

  const isSingleSeriesMode = tooltipMode === 'single';

  const {
    dataIndex,
    seriesIndex,
    seriesName,
    seriesId,
    x,
    y,
    formattedY,
    markerColor,
    markerType,
    shouldEmphasizeText,
  } = emphasizedSeries;

  if (dataIndex === null || seriesIndex === null) return null;
  const key = seriesIndex.toString() + dataIndex.toString();

  // Do not show synthetic truncated confidence interval series
  if (seriesName === DIFF_BAND_TRUNCATION_SERIES_NAME) {
    return null;
  }

  return (
    <TooltipContainer data-testid="emphasized-series-section">
      <IconButtonRow>
        <IconButtonCell>
          <StyledIconButton size="small" variant="tertiary" onClick={onIncreaseHeight}>
            <DotsHorizontal />
          </StyledIconButton>
        </IconButtonCell>
      </IconButtonRow>
      <SeriesInfo
        key={key}
        seriesId={seriesId}
        seriesName={seriesName}
        seriesIndex={seriesIndex}
        y={y}
        x={x}
        links={tooltipConfig.links}
        formattedY={formattedY}
        markerColor={markerColor}
        markerType={markerType}
        config={getSeriesConfig && getSeriesConfig(seriesId)}
        totalSeries={totalSeries}
        wrapSeriesName={shouldEmphasizeText || (wrapSeriesName ?? isSingleSeriesMode)}
        emphasizeText={shouldEmphasizeText}
        tooltipContextMenuProps={tooltipContextMenuProps}
        tooltipContextMenuData={tooltipContextMenuData}
        isTooltipPinned={props.isTooltipPinned}
        unit={unit}
        clearPinned={clearPinned}
        enableTooltipPreciseValues={enableTooltipPreciseValues}
        appMetadata={appMetadata}
      />
    </TooltipContainer>
  );
}

const TooltipContainer = styled('div')({
  maxWidth: '100%',
});

const IconButtonRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const IconButtonCell = styled('div')(({ theme }) => ({
  paddingBottom: theme.spacing(0.5),
}));

const StyledIconButton = styled(IconButton)({
  padding: 0,
});
